import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('villapilot_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
};

// Properties
export const propertiesAPI = {
  getAll: () => api.get('/properties'),
  getOne: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  updateBalance: (id, amount) => api.patch(`/properties/${id}/balance`, null, { params: { amount } }),
  getIncome: (propertyId) => api.get(`/rental-income/${propertyId}`),
  delete: (id) => api.delete(`/properties/${id}`),
  update: (id, data) => api.patch(`/properties/${id}`, data),
};

// Expenses
export const expensesAPI = {
  getAll: (propertyId) => api.get('/expenses', { params: { property_id: propertyId } }),
  getByProperty: (propertyId) => api.get('/expenses', { params: { property_id: propertyId } }),
  create: (data) => api.post('/expenses', data),
  approve: (id, status) => api.patch(`/expenses/${id}/approve`, { status }),
};

// Maintenance
export const maintenanceAPI = {
  getAll: (propertyId) => api.get('/maintenance', { params: { property_id: propertyId } }),
  create: (data) => api.post('/maintenance', data),
  update: (id, data) => api.patch(`/maintenance/${id}`, data),
};

// Inventory
export const inventoryAPI = {
  getAll: (propertyId) => api.get('/inventory', { params: { property_id: propertyId } }),
  getByProperty: (propertyId) => api.get('/inventory', { params: { property_id: propertyId } }),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.patch(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
};

// Financial
export const financialAPI = {
  getSummary: (propertyId) => api.get(`/financial/summary/${propertyId}`),
};

// AI
export const aiAPI = {
  chat: (data) => api.post('/ai/chat', data),
  getValuation: (propertyId) => api.get(`/ai/valuation/${propertyId}`),
  analyzeInventory: (data) => api.post('/ai/inventory/analyze', data),
};

// Reports
export const reportsAPI = {
  getHistory: (propertyId, reportType) => api.get('/reports/history', { 
    params: { property_id: propertyId, report_type: reportType } 
  }),
  downloadReport: (reportId) => api.get(`/reports/download/${reportId}`, { responseType: 'blob' }),
  deleteReport: (reportId) => api.delete(`/reports/history/${reportId}`),
  generateFinancialPDF: (propertyId) => `${BACKEND_URL}/api/reports/financial/${propertyId}/pdf`,
  generateFinancialExcel: (propertyId) => `${BACKEND_URL}/api/reports/financial/${propertyId}/excel`,
};

// Users
export const usersAPI = {
  getManagers: () => api.get('/users/managers'),
  assignProperty: (userId, propertyId) => api.post(`/users/${userId}/properties/${propertyId}`),
};

// Vendor Contracts
export const vendorContractsAPI = {
  getAll: (propertyId) => api.get('/vendor-contracts', { params: { property_id: propertyId } }),
  create: (data) => api.post('/vendor-contracts', data),
  update: (id, data) => api.put(`/vendor-contracts/${id}`, data),
  delete: (id) => api.delete(`/vendor-contracts/${id}`),
};

// Price Alerts
export const priceAlertsAPI = {
  getAll: (propertyId, status) => api.get('/price-alerts', { params: { property_id: propertyId, status } }),
  getSummary: (propertyId) => api.get('/price-alerts/summary', { params: { property_id: propertyId } }),
  dismiss: (id) => api.put(`/price-alerts/${id}/dismiss`),
  resolve: (id, data) => api.put(`/price-alerts/${id}/resolve`, data),
};

export default api;